/**
 * Utility functions for formatting and processing portfolio data
 */

/**
 * Format a date string to a readable format
 * @param {string} dateString - ISO date string
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format a date string to a relative time format (e.g., "2 years ago")
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted relative time or formatted date if older than 1 year
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);
    
    // For dates older than 1 year, show Month Year format
    if (diffYears >= 1) {
      return new Intl.DateTimeFormat('en-US', { 
        month: 'long',
        year: 'numeric' 
      }).format(date);
    }
    
    // For dates within a year but older than a month
    if (diffMonths >= 1) {
      return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    }
    
    // For dates within a month but older than a day
    if (diffDays >= 1) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }
    
    // For today
    return 'today';
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return dateString;
  }
};

/**
 * Format currency value
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === undefined || amount === null) return '';
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${amount}`;
  }
};

/**
 * Format portfolio items for display
 * @param {Array} items - Raw portfolio items from database
 * @returns {Array} - Formatted portfolio items
 */
export const formatPortfolioItems = (items) => {
  if (!Array.isArray(items)) return [];
  
  return items.map(item => ({
    ...item,
    // Map field names for compatibility with PortfolioGallery component
    project_type: item.project_type || item.type,
    thumbnail: item.thumbnail || item.image_url,
    live_url: item.live_url || item.project_url,
    source_url: item.source_url || item.github_url,
    // Convert snake_case to camelCase for frontend consistency
    isPublic: item.is_public,
    isFeatured: item.is_featured,
    projectType: item.project_type || item.type,
    liveUrl: item.live_url || item.project_url,
    sourceUrl: item.source_url || item.github_url,
    // Format dates
    createdAt: formatDate(item.created_at),
    updatedAt: formatDate(item.updated_at),
    // Handle technologies array
    technologies: formatTechnologies(item.technologies),
    // Parse JSONB arrays if needed
    images: parseJsonbArrayIfNeeded(item.images)
  }));
};

/**
 * Format technologies array
 * @param {Array|string|null} technologies - Technologies value to format
 * @returns {Array} - Formatted technologies array
 */
const formatTechnologies = (technologies) => {
  if (!technologies) return [];
  if (Array.isArray(technologies)) return technologies;
  if (typeof technologies === 'string') {
    return technologies.split(',').map(t => t.trim());
  }
  return [];
};

/**
 * Parse JSONB array if needed
 * @param {Array|string|null} value - Value to parse
 * @returns {Array} - Parsed array
 */
const parseJsonbArrayIfNeeded = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  
  try {
    if (typeof value === 'string') {
      return JSON.parse(value);
    }
    return [];
  } catch (error) {
    console.error('Error parsing JSONB array:', error);
    return [];
  }
};

/**
 * Format profile data for display
 * @param {Object} profile - Raw profile data from database
 * @returns {Object} - Formatted profile data
 */
export const formatProfileData = (profile) => {
  if (!profile) return null;
  
  // Calculate derived metrics based on profile data
  const metrics = calculateProfileMetrics(profile);
  
  return {
    ...profile,
    // Format dates
    createdAt: formatDate(profile.created_at),
    updatedAt: formatDate(profile.updated_at),
    // Format join date for profile display
    joinDate: formatRelativeTime(profile.created_at),
    // Format rates
    formattedHourlyRate: formatCurrency(profile.hourly_rate),
    formattedMinimumRate: formatCurrency(profile.minimum_rate),
    formattedMaximumRate: formatCurrency(profile.maximum_rate),
    // Ensure arrays
    skills: Array.isArray(profile.skills) ? profile.skills : [],
    languages: Array.isArray(profile.languages) ? profile.languages : [],
    certifications: Array.isArray(profile.certifications) ? profile.certifications : [],
    softSkills: Array.isArray(profile.soft_skills) ? profile.soft_skills : [],
    workSamples: Array.isArray(profile.work_samples) ? profile.work_samples : [],
    preferredProjectTypes: Array.isArray(profile.preferred_project_types) ? profile.preferred_project_types : [],
    portfolioLinks: Array.isArray(profile.portfolio_links) ? profile.portfolio_links : [],
    industrySpecialization: Array.isArray(profile.industry_specialization) ? profile.industry_specialization : [],
    // Preserve all text fields
    bio: profile.bio || '',
    title: profile.title || '',
    education: profile.education || '',
    workExperience: Array.isArray(profile.work_experience) ? profile.work_experience : [],
    availabilityHours: profile.availability_hours || '',
    workPace: profile.work_pace || '',
    feedbackPreference: profile.feedback_preference || '',
    collaborationStyle: profile.collaboration_style || '',
    communicationStyle: profile.communication_style || '',
    problemSolvingApproach: profile.problem_solving_approach || '',
    workStyleDescription: profile.work_style_description || '',
    yearsOfExperienceCategory: profile.years_of_experience_category || '',
    videoIntroductionUrl: profile.video_introduction_url || null,
    portfolioUrl: profile.portfolio_url || '',
    portfolioDescription: profile.portfolio_description || '',
    preferredCurrency: profile.preferred_currency || 'USD',
    // Preserve video introduction URL
    video_introduction_url: profile.video_introduction_url || null,
    // Add derived metrics
    rating: metrics.rating,
    reviewCount: metrics.reviewCount,
    completedJobs: metrics.completedJobs,
    responseTime: metrics.responseTime,
    clientCount: metrics.clientCount,
    workExperienceCount: metrics.workExperienceCount
  };
};

/**
 * Calculate metrics from profile data
 * Uses precomputed metrics from va_profile_stats view if available
 * @param {Object} profile - Raw profile data from va_profile_stats view
 * @returns {Object} - Metrics object with rating, reviewCount, etc.
 */
const calculateProfileMetrics = (profile) => {
  if (!profile) return {};
  
  // Default metrics
  const metrics = {
    rating: '0.0',
    reviewCount: 0,
    completedJobs: 0,
    responseTime: '24h',
    clientCount: 0,
    workExperienceCount: 0
  };
  
  // Use precomputed metrics from va_profile_stats view
  if (profile.rating !== undefined) {
    metrics.rating = parseFloat(profile.rating).toFixed(1);
    metrics.reviewCount = profile.review_count || 0;
  }
  
  if (profile.completed_jobs !== undefined) {
    metrics.completedJobs = profile.completed_jobs;
  }
  
  if (profile.client_count !== undefined) {
    metrics.clientCount = profile.client_count;
  }
  
  if (profile.work_experience_count !== undefined) {
    metrics.workExperienceCount = profile.work_experience_count;
  } else if (profile.work_experience && Array.isArray(profile.work_experience)) {
    metrics.workExperienceCount = profile.work_experience.length;
  }
  
  // Calculate response time based on experience level
  if (profile.experience_years) {
    if (profile.experience_years > 3) {
      metrics.responseTime = '4h';
    } else if (profile.experience_years > 1) {
      metrics.responseTime = '8h';
    }
  }
  
  return metrics;
};

/**
 * Get appropriate icon for a technology or category
 * @param {string} name - Technology or category name
 * @returns {string} - Icon name
 */
export const getTechnologyIcon = (name) => {
  if (!name) return 'Code';
  
  const lowercaseName = name.toLowerCase();
  
  // Map common technologies to icons
  const iconMap = {
    'react': 'React',
    'javascript': 'Javascript',
    'typescript': 'Typescript',
    'html': 'Html',
    'css': 'Css',
    'node': 'Node',
    'python': 'Python',
    'design': 'Design',
    'figma': 'Figma',
    'photoshop': 'Photoshop',
    'wordpress': 'Wordpress',
    'database': 'Database',
    'marketing': 'Megaphone',
    'writing': 'Pencil',
    'social media': 'Share',
    'video': 'Video',
    'audio': 'Music',
    'excel': 'Table',
    'word': 'FileText',
    'powerpoint': 'Presentation'
  };
  
  // Find matching technology
  for (const [tech, icon] of Object.entries(iconMap)) {
    if (lowercaseName.includes(tech)) {
      return icon;
    }
  }
  
  // Default icons by category
  const categoryIconMap = {
    'development': 'Code',
    'design': 'Palette',
    'marketing': 'TrendingUp',
    'writing': 'FileText',
    'administrative': 'Clipboard',
    'data': 'BarChart',
    'other': 'Star'
  };
  
  // Find matching category
  for (const [category, icon] of Object.entries(categoryIconMap)) {
    if (lowercaseName.includes(category)) {
      return icon;
    }
  }
  
  return 'Star'; // Default icon
};

/**
 * Format skill level to display text and color
 * @param {string|number} level - Skill level or proficiency
 * @returns {Object} - Formatted level with text and color
 */
export const formatSkillLevel = (level) => {
  // Handle numeric proficiency (0-100)
  if (typeof level === 'number' || !isNaN(Number(level))) {
    const proficiency = Number(level);
    
    if (proficiency >= 90) {
      return { text: 'Expert', color: 'text-green-600' };
    } else if (proficiency >= 70) {
      return { text: 'Advanced', color: 'text-blue-600' };
    } else if (proficiency >= 40) {
      return { text: 'Intermediate', color: 'text-yellow-600' };
    } else {
      return { text: 'Beginner', color: 'text-gray-600' };
    }
  }
  
  // Handle string level
  if (typeof level === 'string') {
    switch (level.toLowerCase()) {
      case 'expert':
        return { text: 'Expert', color: 'text-green-600' };
      case 'advanced':
        return { text: 'Advanced', color: 'text-blue-600' };
      case 'intermediate':
        return { text: 'Intermediate', color: 'text-yellow-600' };
      case 'beginner':
        return { text: 'Beginner', color: 'text-gray-600' };
      default:
        return { text: level, color: 'text-gray-600' };
    }
  }
  
  return { text: 'Intermediate', color: 'text-yellow-600' };
};
